<script setup lang="ts">
import { mdiArrowLeft } from '@mdi/js'
// DESIGN.md section 9.1, screen 4 detail view — per-game scores plus what the section 7 contract
// actually exposes about points. NOTE (deviation, see web/README.md): section 9.1 also asks for
// a "point timeline by side/source", but the frozen Match/Game shape in section 7 has no point
// log — only aggregate game scores and `lastPoint` (the single most recent point). There is no
// endpoint to fetch the full ordered point list, so this shows a per-game summary (first server,
// duration, winner) instead of fabricating a timeline the backend can't provide.
import { computed, onMounted, ref } from 'vue'
import Avatar from '../components/Avatar.vue'
import { getMatch } from '../backend'
import { nav, navigate } from '../store/nav'
import type { Match } from '../types'

const match = ref<Match | null>(null)
const loadError = ref(false)

async function load() {
  const id = nav.params.matchId
  if (id == null) {
    loadError.value = true
    return
  }
  try {
    match.value = await getMatch(id)
  } catch {
    loadError.value = true
  }
}

onMounted(load)

const winnerName = computed(() => {
  const m = match.value
  if (!m || m.winnerPlayerId == null) return null
  return m.winnerPlayerId === m.players.left.id ? m.players.left.displayName : m.players.right.displayName
})

function ratingLine(playerId: number): string {
  const rc = match.value?.ratingChanges?.[playerId]
  if (!rc) return '—'
  const sign = rc.delta >= 0 ? '+' : ''
  return `${rc.before} → ${rc.after} (${sign}${rc.delta})`
}

function duration(startedAt: string, finishedAt: string | null): string {
  if (!finishedAt) return ''
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime()
  const mins = Math.round(ms / 60000)
  return `${mins}m`
}

function goBack() {
  navigate('pastMatches')
}
</script>

<template>
  <div class="flex-1 min-h-0 flex flex-col p-4 gap-4 overflow-y-auto">
    <div class="flex items-center gap-3">
      <button
        type="button"
        class="tap-target w-10 h-10 min-h-0 min-w-0 shrink-0 rounded-full active:bg-neutral-800 flex items-center justify-center text-neutral-400"
        aria-label="Back to matches"
        @click="goBack"
      >
        <svg viewBox="0 0 24 24" class="w-5 h-5" fill="currentColor"><path :d="mdiArrowLeft" /></svg>
      </button>
      <h1 class="text-xl font-bold flex-1">Match detail</h1>
    </div>

    <div v-if="loadError" class="text-neutral-500">Could not load this match</div>

    <template v-else-if="match">
      <div class="rounded-2xl bg-neutral-900 border border-neutral-800 p-4 flex items-center gap-6">
        <div class="flex flex-col items-center gap-2 flex-1">
          <Avatar :player="match.players.left" :size="56" />
          <span class="font-semibold">{{ match.players.left.displayName }}</span>
          <span class="font-mono text-sm text-emerald-300">{{ ratingLine(match.players.left.id) }}</span>
        </div>
        <div class="flex flex-col items-center gap-1">
          <span v-if="match.status === 'abandoned'" class="text-neutral-500 uppercase text-sm font-bold">Abandoned</span>
          <span v-else class="text-emerald-400 font-bold">{{ winnerName }} won</span>
          <span class="text-2xl font-mono font-black">{{ match.gamesWon[match.players.left.id] ?? 0 }}–{{ match.gamesWon[match.players.right.id] ?? 0 }}</span>
          <span class="text-xs text-neutral-500">{{ match.matchType.name }} · Best of {{ match.bestOf }}</span>
        </div>
        <div class="flex flex-col items-center gap-2 flex-1">
          <Avatar :player="match.players.right" :size="56" />
          <span class="font-semibold">{{ match.players.right.displayName }}</span>
          <span class="font-mono text-sm text-emerald-300">{{ ratingLine(match.players.right.id) }}</span>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <h2 class="text-sm font-bold text-neutral-400 uppercase tracking-wide">Games</h2>
        <div v-for="g in match.games" :key="g.index" class="rounded-xl bg-neutral-900 border border-neutral-800 px-4 py-3 flex items-center gap-4">
          <span class="text-neutral-500 font-mono w-16">Game {{ g.index + 1 }}</span>
          <span class="font-mono text-lg font-bold flex-1">{{ g.players.left.displayName }} {{ g.scores.left }}–{{ g.scores.right }} {{ g.players.right.displayName }}</span>
          <span class="text-xs text-neutral-500">First serve: {{ g.firstServer === 'left' ? g.players.left.displayName : g.players.right.displayName }}</span>
          <span class="text-xs text-neutral-500">{{ duration(g.startedAt, g.finishedAt) }}</span>
        </div>
      </div>
    </template>
  </div>
</template>
